import React, { useEffect, useState } from "react";
import { sortingFns } from "@tanstack/react-table";
import { compareItems } from "@tanstack/match-sorter-utils";

import MasterTable from "../../../components/master/MasterTable";
import ComponentLoader from "../../../components/ui/pageLoader/ComponentLoader";

import { contractAddIcon, downloadIcon } from "../../../assetData/icons";
import { actionPerm } from "../../../components/authNavigation/AuthNavbar";
import { format } from "date-fns";
import { sortData } from "../../../utils/sortData";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { downloadLoader } from "../../../assetData/loaders";

const fuzzySort = (rowA, rowB, columnId) => {
  let dir = 0;

  // Only sort by rank if the column has ranking information
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank,
      rowB.columnFiltersMeta[columnId]?.itemRank,
    );
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

const MasterScriptTable = (props) => {
  const {
    tableData,
    buttonConfig,
    setIsAdd,
    setIsEdit,
    setIsDelete,
    setIsImport,
    setSelectedItemId,
    renderKey,
    setGlobalFilters,
    loading,
    actionPermissions,
    setIsFilter,
    isFilter,
    isDownloadClicked,
  } = props;

  const [tableFilter, setTableFilter] = useState(tableData);
  const [sortingType, setSortingType] = useState("asc");
  const [sortColumn, setSortColumn] = useState("script_name");

  useEffect(() => {
    setTableFilter(tableData);
  }, [tableData]);

  const handleSorting = (column, sortType, type) => {
    setSortColumn(column);

    const sortedData = sortData({
      data: tableData,
      column,
      type,
      sortType,
    });

    setTableFilter(sortedData);
    setSortingType(sortingType === "asc" ? "desc" : "asc");
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "sl",
        header: () => <div className="inline-block w-[30px] pl-2">SR. NO.</div>,
        cell: (info) => <div className="w-[30px]">{info.row.index + 1}.</div>,
      },
      {
        accessorKey: "script_name",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "script_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex w-[110px] justify-between "
          >
            <div>SCRIP NAME</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "script_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "script_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div></div>,
      },

      {
        accessorFn: (row) => row.isin,
        id: "isin",
        cell: (info) => info.getValue(),
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "isin",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>ISIN</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "isin" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "isin" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },

      {
        accessorKey: "nse_symbol",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "nse_symbol",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className="flex w-full max-w-[200px] justify-between pl-1"
          >
            <div>NSE SYMBOL</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "nse_symbol" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "nse_symbol" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div> {info.getValue()} </div>,
      },

      {
        accessorKey: "nse_series",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "nse_series",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>
              {" "}
              <div className="">NSE</div>
              <div className="">SERIES</div>
            </div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "nse_series" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "nse_series" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div> {info.getValue()} </div>,
      },

      {
        accessorKey: "nse_lot",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "nse_lot",
                sortingType === "asc" ? "desc" : "asc",
                "number",
              );
            }}
            className=" flex justify-between pl-1 "
          >
            <div>
              {" "}
              <div className="">NSE</div>
              <div className="">LOT</div>
            </div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "nse_lot" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "nse_lot" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div>
            {parseInt(info.getValue())
              ? parseInt(info.getValue())
              : info.getValue()}
          </div>
        ),
      },

      {
        accessorKey: "nse_face_value",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "nse_face_value",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>
              <div className="">NSE</div>
              <div className="">FV</div>
            </div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "nse_face_value" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "nse_face_value" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div>
            {info.getValue() !== "nan" || !info.getValue()
              ? Number(info.getValue()).toFixed(2)
              : ""}
          </div>
        ),
      },

      {
        accessorKey: "bse_code",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "bse_code",
                sortingType === "asc" ? "desc" : "asc",
                "number",
              );
            }}
            className=" flex justify-between pl-1 "
          >
            <div>
              <div className="">BSE</div>
              <div className="">CODE</div>
            </div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "bse_code" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "bse_code" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div> {info.getValue()} </div>,
      },

      {
        accessorKey: "bse_lot",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "bse_lot",
                sortingType === "asc" ? "desc" : "asc",
                "number",
              );
            }}
            className=" flex justify-between pl-1 "
          >
            <div>
              <div className="">NSE</div>
              <div className="">LOT</div>
            </div>{" "}
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "bse_lot" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "bse_lot" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div>
            {parseInt(info.getValue())
              ? parseInt(info.getValue())
              : info.getValue()}
          </div>
        ),
      },

      {
        accessorKey: "bse_face_value",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "bse_face_value",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between pl-1 "
          >
            <div>
              <div className="">BSE</div>
              <div className="">FV</div>
            </div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "bse_face_value" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "bse_face_value" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div> {formatNumber(info.getValue())} </div>,
      },

      {
        accessorKey: "bse_group",

        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "bse_group",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>
              <div className="">BSE</div>
              <div className="">GROUP</div>
            </div>{" "}
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "bse_group" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "bse_group" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div> {info.getValue()} </div>,
      },

      {
        accessorKey: "script_type_name",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "script_type_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>
              <div className="">SECURITY</div>
              <div className="">TYPE</div>
            </div>{" "}
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "script_type_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "script_type_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div> {info.getValue()} </div>,
      },

      {
        accessorKey: "asset_class_name",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "asset_class_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>
              <div className="">ASSET</div>
              <div className="">TYPE</div>
            </div>{" "}
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "asset_class_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "asset_class_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div> {info.getValue()} </div>,
      },

      {
        accessorKey: "vidal_sector_code",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "vidal_sector_code",
                sortingType === "asc" ? "desc" : "asc",
                "number",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>
              <div className="">VIDAL</div>
              <div className="">CODE</div>
            </div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "vidal_sector_code" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "vidal_sector_code" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <div> {info.getValue()} </div>,
      },

      {
        accessorKey: "ipo_allotment_date",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "ipo_allotment_date",
                sortingType === "asc" ? "desc" : "asc",
                "date",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>
              <div className="">IPO ALLOT.</div>
              <div className="">DATE</div>
            </div>{" "}
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "ipo_allotment_date" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "ipo_allotment_date" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div>
            {" "}
            {info.getValue() &&
              format(new Date(info.getValue()), "dd MMM yyyy")}{" "}
          </div>
        ),
      },

      {
        accessorKey: "ipo_allotment_price",
        header: () => (
          <div className=" text-right">
            <div className="">IPO ALLOT.</div>
            <div className="">PRICE</div>
          </div>
        ),
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "ipo_allotment_price",
                sortingType === "asc" ? "desc" : "asc",
                "number",
              );
            }}
            className=" flex justify-between pl-1"
          >
            <div>
              <div className="">IPO ALLOT.</div>
              <div className="">PRICE</div>
            </div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "ipo_allotment_price" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "ipo_allotment_price" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => (
          <div className="text-right">
            {info.getValue() && Number(info.getValue()).toFixed(2)}{" "}
          </div>
        ),
      },

      {
        accessorKey: "status",
        header: () => <div className="pl-1 text-center">STATUS</div>,
        cell: (info) => <div className=" text-center"> {info.getValue()} </div>,
      },

      // {
      //   accessorKey: "last_updated_by",
      //   header: () => <div>LAST UPDATED BY</div>,
      //   cell: (info) => <div> {info.getValue()} </div>,
      // },

      {
        accessorKey: "id",
        header: () => <div className="text-center"> ACTIONS </div>,
        cell: (info) => <div className="w-[60px] text-center"> </div>,
      },
    ],
    [sortColumn, sortingType, tableData, tableFilter],
  );

  const handleDownloadButtonClick = () => {
    buttonConfig.downloadHandler();
  };

  const actionConfig = [
    // {
    //   id: 1,
    //   name: "Fetch From Mail",
    //   image: contractAddIcon,
    // },
    {
      id: 2,
      name: "Import Data",
      image: contractAddIcon,
      actionHandler: () => setIsImport(true),
      disable: actionPerm(actionPermissions)?.import_perm,
    },
    {
      id: 3,
      name: "Add Scrip",
      image: contractAddIcon,
      actionHandler: () => setIsAdd(true),
      disable: actionPerm(actionPermissions)?.add_perm,
    },

    {
      id: 4,
      name: "Download Data",
      image: isDownloadClicked ? downloadLoader : downloadIcon,
      actionHandler: isDownloadClicked
        ? () => {}
        : buttonConfig.downloadHandler,
      disable: actionPerm(actionPermissions)?.download_perm,
    },
  ];

  function formatNumber(number) {
    let formattedNumber = number?.toString();

    // If it doesn't have a decimal point, add ".0"
    if (formattedNumber?.indexOf(".")) {
      formattedNumber += "0";
    }

    return formattedNumber;
  }
  // if (!tableData) return null;
  const action = actionPerm(actionPermissions);

  return (
    <MasterTable
      columns={columns}
      setIsAdd={setIsAdd}
      setIsImport={setIsImport}
      tableData={tableFilter?.length > 0 ? tableFilter : tableData}
      buttonConfig={buttonConfig}
      setIsEdit={setIsEdit}
      setIsDelete={setIsDelete}
      setSelectedItemId={setSelectedItemId}
      renderKey={renderKey}
      loading={loading}
      filterField
      actionConfig={actionConfig}
      setGlobalFilters={setGlobalFilters}
      handleDownloadButtonClick={handleDownloadButtonClick}
      actions={action}
      scripPage={true}
      name={"scrip"}
      setIsFilter={setIsFilter}
      isFilter={isFilter}
      setSelectedItem={false}
    />
  );
};

export default MasterScriptTable;
